import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import CustomerVoiceSection from '@/components/feature/CustomerVoiceSection';
import { cases } from '@/mocks/cases';

function FadeIn({ children, delay = 0 }: { children: React.ReactNode; delay?: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.08 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  return (
    <div
      ref={ref}
      className="transition-all duration-700"
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(24px)',
        transitionDelay: `${delay}ms`,
      }}
    >
      {children}
    </div>
  );
}

export default function CasesPage() {
  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <section className="pt-32 pb-20 bg-[#0F172A] relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="/bg-tech-dark.svg"
            alt="cases hero"
            className="w-full h-full object-cover object-top opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0F172A]/60 to-[#0F172A]" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10">
          <div className="max-w-2xl">
            <span className="text-xs uppercase tracking-widest text-white/40 mb-4 block" style={{ fontFamily: 'Inter, sans-serif' }}>
              Case Studies
            </span>
            <h1
              className="text-4xl md:text-6xl font-bold text-white leading-tight mb-6"
              style={{ fontFamily: '\'Noto Sans SC\', sans-serif', letterSpacing: '-0.02em' }}
            >
              案例
            </h1>
            <p
              className="text-lg text-white/50 leading-relaxed"
              style={{ fontFamily: '\'Noto Sans SC\', sans-serif' }}
            >
              真实项目，真实过程。我们不展示成果截图，而是展示方法落地的过程，以及客户能带走的资产与能力。
            </p>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="bg-[#F8FAFC] border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-8">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-6">
            {[
              { label: '行业覆盖', items: ['企业应用', 'SaaS 服务', '信息安全', '网络监控', '房地产', '律所', '医疗'] },
              { label: '角色覆盖', items: ['产品', '设计', '架构', '前后端开发', '测试', '运维部署'] },
              { label: '周期：1–2 天', items: ['方法论', 'AI 工具实操'] },
              { label: '周期：5–12 天', items: ['能力评估', '方法论', '环境改造', '全流程实操', 'Skill / Agent 定制'] },
              { label: '交付形式', items: ['驻场', '真实项目推进', '真实环境改造'] },
            ].map((s, idx) => (
              <div key={idx}>
                <div
                  className="text-xs font-semibold text-[#F97316] uppercase tracking-widest mb-3"
                  style={{ fontFamily: 'Inter, \'Noto Sans SC\', sans-serif' }}
                >
                  {s.label}
                </div>
                <div className="flex flex-col gap-2">
                  {s.items.map((item) => (
                    <span
                      key={item}
                      className="text-sm text-[#0F172A] leading-relaxed"
                      style={{ fontFamily: '\'Noto Sans SC\', sans-serif' }}
                    >
                      {item}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Cases List */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <FadeIn>
            <h2
              className="text-2xl md:text-3xl font-bold text-[#0F172A] mb-12"
              style={{ fontFamily: '\'Noto Sans SC\', sans-serif', letterSpacing: '-0.01em' }}
            >
              典型案例
            </h2>
          </FadeIn>

          {/* Cards */}
          <div className="flex flex-col gap-8">
            {cases.map((c, idx) => (
              <FadeIn key={c.id} delay={idx * 100}>
                <Link
                  to={`/cases/${c.id}`}
                  className="group block bg-[#F8FAFC] rounded-2xl border border-slate-100 overflow-hidden hover:border-[#2563EB]/20 transition-all duration-300 cursor-pointer"
                >
                  <div className="grid grid-cols-1 lg:grid-cols-5">
                    {/* Image */}
                    <div className="lg:col-span-2 h-56 lg:h-auto overflow-hidden">
                      <img
                        src={c.coverImage}
                        alt={c.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>

                    {/* Content */}
                    <div className="lg:col-span-3 p-8 md:p-10 flex flex-col justify-between">
                      <div>
                        {/* Tag + Meta */}
                        <div className="flex flex-wrap items-center gap-3 mb-5">
                          <span
                            className="text-xs font-semibold px-3 py-1 rounded-full"
                            style={{
                              color: c.tagColor,
                              backgroundColor: `${c.tagColor}15`,
                              fontFamily: '\'Noto Sans SC\', sans-serif',
                            }}
                          >
                            {c.tag}
                          </span>
                          <span className="text-xs text-[#64748B]" style={{ fontFamily: '\'Noto Sans SC\', sans-serif' }}>
                            {c.industry}
                          </span>
                          <span className="text-xs text-[#64748B]" style={{ fontFamily: '\'Noto Sans SC\', sans-serif' }}>
                            {c.teamSize} · {c.duration}
                          </span>
                        </div>

                        <h2
                          className="text-xl md:text-2xl font-bold text-[#0F172A] mb-3 group-hover:text-[#2563EB] transition-colors"
                          style={{ fontFamily: '\'Noto Sans SC\', sans-serif', letterSpacing: '-0.01em' }}
                        >
                          {c.title}
                        </h2>
                        <p
                          className="text-sm text-[#64748B] mb-6"
                          style={{ fontFamily: '\'Noto Sans SC\', sans-serif' }}
                        >
                          {c.subtitle}
                        </p>

                        {/* Problem preview */}
                        <p
                          className="text-sm text-[#64748B] leading-relaxed line-clamp-2"
                          style={{ fontFamily: '\'Noto Sans SC\', sans-serif' }}
                        >
                          {c.problem.summary}
                        </p>
                      </div>

                      {/* Highlights preview */}
                      <div className="mt-8 pt-6 border-t border-slate-100 flex flex-wrap items-start gap-4">
                        {c.result.highlights.slice(0, 3).map((h) => (
                          <div key={h.label} className="flex items-center gap-2">
                            <i className="ri-check-line text-sm text-[#F97316]" />
                            <span
                              className="text-xs text-[#0F172A] font-medium"
                              style={{ fontFamily: '\'Noto Sans SC\', sans-serif' }}
                            >
                              {h.label}
                            </span>
                          </div>
                        ))}
                        <div className="ml-auto flex items-center gap-1.5 text-sm font-medium text-[#64748B] group-hover:text-[#0F172A] transition-colors whitespace-nowrap">
                          <span style={{ fontFamily: '\'Noto Sans SC\', sans-serif' }}>查看完整案例</span>
                          <i className="ri-arrow-right-line text-sm group-hover:translate-x-1 transition-transform" />
                        </div>
                      </div>
                    </div>
                  </div>
                </Link>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* Customer Voices */}
      <CustomerVoiceSection
        variant="gray"
        title="来自真实参训团队的反馈"
        subtitle="以下反馈来自完成培训的客户团队，按职位角色匿名整理。"
      />

      {/* CTA */}
      <section className="py-20 bg-[#0F172A]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 text-center">
          <FadeIn>
            <h2
              className="text-2xl md:text-3xl font-bold text-white mb-4"
              style={{ fontFamily: '\'Noto Sans SC\', sans-serif' }}
            >
              你的项目也可以成为下一个案例
            </h2>
            <p
              className="text-base text-white/50 mb-8"
              style={{ fontFamily: '\'Noto Sans SC\', sans-serif' }}
            >
              预约一次免费的诊断对话，我们帮你判断 AI 能为你的项目带来什么样的可见变化。
            </p>
            <Link
              to="/#contact-section"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-[#F97316] text-white text-sm font-semibold whitespace-nowrap cursor-pointer hover:bg-[#ea6c0a] transition-colors"
              style={{ fontFamily: '\'Noto Sans SC\', sans-serif' }}
            >
              预约交流
              <i className="ri-arrow-right-line" />
            </Link>
          </FadeIn>
        </div>
      </section>

      <Footer />
    </div>
  );
}
